/**
 * German copy for what a demo company refuses (#934), and the two codes the server answers with.
 *
 * A demo company (`Company.IsDemo`, #916) is fictional. Its users and employees come from the demo tools, and
 * `demo remove` deletes the whole company later. So it accepts no invitations and no linked sign-in accounts:
 * a real person let in would keep access to a tenant that is about to disappear. The server refuses both
 * (`API.Core/Authorization/DemoCompanyAuthErrorCodes.cs`); the screens hide or disable the actions and show
 * one of the two hints below in their place.
 *
 * One module for every screen, so the payroll list, the access screen, the employee screen and the profile
 * links cannot explain the same rule four different ways.
 */

/** The two codes, mirrored from `DemoCompanyAuthErrorCodes`. */
export const DemoCompanyErrorCodes = {
  InvitationRefused: 'E_DEMO_COMPANY_INVITATION_REFUSED',
  IdentityLinkRefused: 'E_DEMO_COMPANY_IDENTITY_LINK_REFUSED',
  ContactLinkRefused: 'E_DEMO_COMPANY_CONTACT_LINK_REFUSED',
  ContactRequirementRefused: 'E_DEMO_COMPANY_CONTACT_REQUIREMENT_REFUSED'
} as const;

/** Shown where an invitation would be issued or sent again. */
export const DEMO_COMPANY_INVITATION_HINT =
  'Dies ist ein Demo-Mandant. Hier können Sie niemanden einladen, denn alle Zugänge sind erfunden und werden ' +
  'von den Demo-Werkzeugen angelegt.';

/** Shown where a Microsoft, Google, Apple or e-mail account would be linked to a login of this company. */
export const DEMO_COMPANY_LINK_HINT =
  'Dies ist ein Demo-Mandant. Mit seinen Zugängen lässt sich kein eigenes Konto verknüpfen. Melden Sie sich ' +
  'mit den Demo-Zugangsdaten an.';

/** Shown where a contact channel (Telegram) would be linked to an employee of this company. */
export const DEMO_COMPANY_CONTACT_HINT =
  'Dies ist ein Demo-Mandant. Seine Mitarbeiter sind erfunden, deshalb lässt sich hier kein Kontaktkanal ' +
  'verknüpfen.';

/** Shown where a contact requirement would be created or changed. */
export const DEMO_COMPANY_REQUIREMENT_HINT =
  'Dies ist ein Demo-Mandant. Seine Mitarbeiter können keinen Kontaktkanal verknüpfen, deshalb lassen sich ' +
  'hier keine Pflicht-Kontakte festlegen.';

const MESSAGES: Readonly<Record<string, string>> = {
  [DemoCompanyErrorCodes.InvitationRefused]: DEMO_COMPANY_INVITATION_HINT,
  [DemoCompanyErrorCodes.IdentityLinkRefused]: DEMO_COMPANY_LINK_HINT,
  [DemoCompanyErrorCodes.ContactLinkRefused]: DEMO_COMPANY_CONTACT_HINT,
  [DemoCompanyErrorCodes.ContactRequirementRefused]: DEMO_COMPANY_REQUIREMENT_HINT
};

/**
 * The German sentence for one of the two demo-company codes, or `null` for any other code, so a caller can try
 * this table first and fall back to its own.
 */
export function describeDemoCompanyError(code: string | null | undefined): string | null {
  return (code && MESSAGES[code]) || null;
}
